import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Sticky horizontal scrollbar under the canvas viewport (synced with main scroll area).
 */
export default function CanvasHorizontalScrollbar({ viewportRef, contentWidth = 0 }) {
  const trackRef = useRef(null);
  const [trackW, setTrackW] = useState(0);
  const [spacerW, setSpacerW] = useState(0);
  const [canScroll, setCanScroll] = useState(false);
  const syncingRef = useRef(false);

  const measure = useCallback(() => {
    const vp = viewportRef?.current;
    if (!vp) return;

    const cw = vp.clientWidth;
    const sw = Math.max(vp.scrollWidth, Number(contentWidth) || 0, cw);
    setTrackW(cw);
    setSpacerW(sw);
    setCanScroll(sw > cw + 1);

    const track = trackRef.current;
    if (track && !syncingRef.current) {
      syncingRef.current = true;
      track.scrollLeft = vp.scrollLeft;
      syncingRef.current = false;
    }
  }, [viewportRef, contentWidth]);

  useEffect(() => {
    measure();
    const vp = viewportRef?.current;
    if (!vp) return undefined;

    const ro = new ResizeObserver(() => measure());
    ro.observe(vp);

    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure, viewportRef, contentWidth]);

  useEffect(() => {
    const vp = viewportRef?.current;
    const track = trackRef.current;
    if (!vp || !track) return undefined;

    const onVpScroll = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      track.scrollLeft = vp.scrollLeft;
      syncingRef.current = false;
    };
    const onTrackScroll = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      vp.scrollLeft = track.scrollLeft;
      syncingRef.current = false;
    };

    vp.addEventListener('scroll', onVpScroll, { passive: true });
    track.addEventListener('scroll', onTrackScroll, { passive: true });
    onVpScroll();
    return () => {
      vp.removeEventListener('scroll', onVpScroll);
      track.removeEventListener('scroll', onTrackScroll);
    };
  }, [viewportRef, canScroll, spacerW]);

  return (
    <div
      className="shrink-0 w-full min-w-0 border-t border-slate-300/90 bg-slate-200/95 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]"
      style={{ height: 18 }}
    >
      <div
        ref={trackRef}
        className={`w-full h-full overflow-x-auto overflow-y-hidden canvas-h-scroll-track ${
          canScroll ? '' : 'pointer-events-none'
        }`}
        style={{ width: trackW || '100%' }}
        role="scrollbar"
        aria-orientation="horizontal"
        aria-label="Canvas horizontal scroll"
        aria-hidden={!canScroll}
      >
        <div style={{ width: Math.max(spacerW, trackW || 0), height: 1 }} aria-hidden />
      </div>
    </div>
  );
}
